import { renderPrintToolbar, printToolbarStyles } from "./pdfExportUtils";

/**
 * Utility for generating, printing, and exporting Agreements (Teaming Agreements, NDAs, etc.) to Microsoft Word (.doc) and PDF/Print preview.
 */

function replacePlaceholders(text, variables) {
  if (!text) return "";
  let result = text;
  Object.keys(variables).forEach((key) => {
    const regex = new RegExp(`{{${key}}}`, "g");
    result = result.replace(regex, variables[key] || "_______");
  });
  return result;
}

function formatDateParts(dateStr) {
  if (!dateStr) {
    return { day: "___", month: "________", year: "2026", formatted: "_______" };
  }
  try {
    const d = new Date(dateStr + "T00:00:00");
    const day = d.getDate();
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return {
      day: String(day),
      month,
      year: String(year),
      formatted: `${month} ${day}, ${year}`
    };
  } catch {
    return { day: "___", month: "________", year: "2026", formatted: dateStr };
  }
}

export function generateAgreementDocumentHtml(agreement, template, companyLogo, isWord = false) {
  const dateParts = formatDateParts(agreement.effectiveDate);

  // Prime Company attributes
  const primeCompanyName = agreement.primeCompanyName || "Longarm Technologies, LLC";
  const primeCompanyState = agreement.primeCompanyState || "Nevada";
  const primeCompanyAddress = agreement.primeCompanyAddress || "1543 Frew mill Rd News Castle PA";
  const primeCompanyAcronym = agreement.primeCompanyAcronym || (primeCompanyName.split(" ").map(w => w[0]).join("").toUpperCase()) || "LAT";
  const activeLogo = agreement.primeCompanyLogo || companyLogo || "https://jimini-logistics.web.app/favicon.png";

  const variables = {
    day: dateParts.day,
    month: dateParts.month,
    year: dateParts.year,
    effectiveDate: dateParts.formatted,
    primeCompanyName,
    primeCompanyState,
    primeCompanyAddress,
    primeCompanyAcronym,
    partnerName: agreement.partnerCompany || agreement.partnerName || "_________________________",
    partnerCompany: agreement.partnerCompany || agreement.partnerName || "_________________________",
    partnerState: agreement.partnerState || "Maryland",
    partnerAddress: agreement.partnerAddress || "__________________________________________________",
    partnerAcronym: agreement.partnerAcronym || agreement.partnerName || "Partner",
    customerName: agreement.customerName || "US Government Department of Veterans Affairs",
    customerAcronym: agreement.customerAcronym || "Customer",
    procurementDescription: agreement.procurementDescription || "leased space and the material and services required for the buildout of said leased space",
    procurementTitle: agreement.customerAcronym || agreement.customerName || "VA",
    leadProposalParty: agreement.leadProposalParty || agreement.partnerAcronym || "USPAS",
    clientInterfaceParty: agreement.clientInterfaceParty || primeCompanyAcronym,
    noticesLat: agreement.noticesLatContact || `${primeCompanyName}\n${primeCompanyAddress}\nAttention: ${agreement.signatoryLat?.signatoryName || "Authorized Representative"}\nPhone: (866) 668-6060\nemail: support@longarmtechnologies.com`,
    noticesPartner: agreement.noticesPartnerContact || `${agreement.partnerCompany || "Partner"}\n${agreement.partnerAddress || "Address"}\nAttention: ${agreement.partnerPocName || "Authorized Representative"}\nPhone: ${agreement.partnerPocPhone || "N/A"}\nemail: ${agreement.partnerPocEmail || "N/A"}`
  };

  const sectionsToRender = (agreement.sections && agreement.sections.length > 0)
    ? agreement.sections
    : (template?.sections || []);

  const defaultPreamble = `THIS TEAMING AGREEMENT, including all Exhibits attached hereto or referenced herein (hereinafter referred to as “this Agreement”), is made and entered into this {{day}} day of {{month}}, {{year}}, by and between ${primeCompanyName} (“${primeCompanyAcronym}”), a ${primeCompanyState} corporation with offices located at ${primeCompanyAddress}, and {{partnerName}} a {{partnerState}} corporation with offices located at {{partnerAddress}}. These two entities are sometimes referred to collectively herein as the “Team or Parties” and individually as a “Party.”`;
  
  const preambleRaw = template?.preambleTemplate ? template.preambleTemplate.replace(/Longarm Technologies, LLC/g, primeCompanyName).replace(/LAT/g, primeCompanyAcronym) : defaultPreamble;
  const preambleHtml = replacePlaceholders(preambleRaw, variables);

  const recitalsRaw = template?.recitals || [
    `WHEREAS, the {{customerName}}, herein referred to as “Customer” or {{customerAcronym}} is planning to issue solicitations (“Solicitations”) for the acquisition of {{procurementDescription}} (“Procurement”).`,
    `WHEREAS, each of the Parties has determined that it would benefit from a team arrangement in order to develop the optimal approach to responding to each Solicitation, and because of each Party’s unique capabilities, the Parties recognize the efficiency of teaming together and therefore wish to team together for the purpose of competitively responding to each Solicitation and obtaining contract(s) and/or purchase/task order(s) resulting therefrom (herein referred to as “Orders”);`,
    `WHEREAS, the Parties wish to enter into this Agreement to set forth more fully the terms and conditions pursuant to which the Parties will, as a Team, respond to the Solicitations and obtain any Order(s) resulting therefrom, and the Parties have agreed to the respective responsibilities of work to be performed by each Party on the Procurement, as set forth in the Solicitation; and`,
    `WHEREAS, the ability of the Parties to competitively respond to the Solicitations and to obtain contracts resulting therefrom will necessitate the disclosure by each Party to the other of its Proprietary Information, and the Parties are willing to make their Proprietary Information available to the other Party for the limited purpose of competitively responding to the Solicitations and obtaining order(s) resulting therefrom, and each of the Parties is willing to accept the Proprietary Information from the other Party in confidence for use solely and exclusively in connection with competitively responding to the Solicitations and obtaining orders(s) resulting therefrom, in accordance with the terms and conditions of this Agreement and any Non-Disclosure Agreement the Parties have entered into.`
  ];

  const recitalsHtml = recitalsRaw.map((rec) => {
    const text = replacePlaceholders(rec, variables);
    return `<p style="margin-bottom: 10px; text-align: justify; text-justify: inter-word;">${text}</p>`;
  }).join("\n");

  const wordHeader = isWord ? `
    <!--[if gte mso 9]>
    <xml>
      <w:WordDocument>
        <w:View>Print</w:View>
        <w:Zoom>100</w:Zoom>
      </w:WordDocument>
    </xml>
    <![endif]-->
  ` : "";

  // Signature rendering helpers
  const sigLat = agreement.signatoryLat || {};
  const sigPartner = agreement.signatoryPartner || {};

  const renderSignatureBlock = (sig, defaultCompany, defaultName, defaultTitle) => {
    const isSigned = Boolean(sig.signatureAdopted);
    const sigName = sig.signatureText || sig.signatoryName || defaultName;
    const dateStr = sig.date || dateParts.formatted;

    return `
      <td style="width: 50%; vertical-align: top; padding: 10px 20px; border: none; font-size: 10.5pt;">
        <strong style="color: #17365D; font-size: 11pt; display: block; margin-bottom: 6px;">
          ${sig.company || defaultCompany}
        </strong>

        ${isSigned ? `
          <div style="min-height: 38px; display: flex; align-items: flex-end;">
            <div style="font-family: 'Brush Script MT', 'Segoe Script', 'Dancing Script', cursive; font-size: 22pt; color: #17365D; font-style: italic; line-height: 1; margin-bottom: -4px;">
              ${sigName}
            </div>
          </div>
          <div class="sig-line" style="border-bottom: 1px solid #111; height: 4px; margin-bottom: 5px;"></div>
          <div style="font-size: 8pt; color: #059669; font-weight: bold; margin-bottom: 4px;">
            ✓ Digitally Adopted & Signed by ${sig.signatoryName || sigName} (${dateStr})
          </div>
        ` : `
          <div class="sig-line" style="border-bottom: 1px solid #111; height: 35px; margin-bottom: 5px;"></div>
          <div style="display: flex; justify-content: space-between; font-size: 10pt; color: #4a5568;">
            <span>Authorized Signature</span>
            <span>Date: ${dateStr}</span>
          </div>
        `}

        <div style="margin-top: 6px; font-size: 10.5pt; font-weight: 600;">
          ${sig.signatoryName || defaultName}
        </div>
        <div style="font-size: 10pt; color: #555;">
          ${sig.signatoryTitle || defaultTitle}
        </div>
      </td>
    `;
  };

  return `
    <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
    <head>
      <title>${agreement.title || template?.title || "Teaming Agreement"}</title>
      <meta charset="utf-8">
      ${wordHeader}
      <style>
        body {
          font-family: 'Times New Roman', Times, serif;
          font-size: 11pt;
          line-height: 1.5;
          color: #111111;
          margin: 0;
          padding: 30px 40px;
          background: #ffffff;
        }
        h1.doc-title {
          font-size: 16pt;
          font-weight: bold;
          text-align: center;
          text-transform: uppercase;
          margin: 0 0 20px 0;
          color: #17365D;
          letter-spacing: 0.5px;
        }
        p {
          margin: 0 0 12px 0;
          text-align: justify;
          text-justify: inter-word;
        }
        .section-item {
          margin-bottom: 14px;
        }
        .section-title {
          font-weight: bold;
          display: inline;
        }
        .section-content {
          text-align: justify;
          text-justify: inter-word;
          white-space: pre-wrap;
          display: block;
          margin-top: 4px;
        }
        .signature-table {
          width: 100%;
          margin-top: 35px;
          border-collapse: collapse;
          border: none;
          page-break-inside: avoid;
        }
        .signature-table td {
          width: 50%;
          vertical-align: top;
          padding: 10px 20px;
          border: none;
          font-size: 10.5pt;
        }
        .sig-line {
          border-bottom: 1px solid #111;
          height: 35px;
          margin-bottom: 5px;
        }
        ${!isWord ? printToolbarStyles : ""}
        @media print {
          body { padding: 0.5in; }
          .no-print { display: none; }
          @page { margin: 0.6in; size: portrait; }
        }
      </style>
    </head>
    <body>
      ${!isWord ? renderPrintToolbar(agreement.title || "Agreement Document Preview") : ""}
      <div style="display: flex; justify-content: flex-start; text-align: left; margin-bottom: 15px;">
        <img src="${activeLogo}" style="max-height: 48px; max-width: 180px; object-fit: contain;" alt="Company Logo" />
      </div>

      <h1 class="doc-title">${agreement.title || template?.title || "TEAMING AGREEMENT"}</h1>

      <p style="font-weight: 500; margin-bottom: 14px; text-align: justify; text-justify: inter-word;">
        ${preambleHtml}
      </p>

      ${recitalsHtml}

      <p style="font-weight: bold; margin: 16px 0 12px 0; text-align: justify;">
        NOW, THEREFORE, in consideration of the foregoing, and in reliance on the mutual promises and obligations contained herein, the Parties hereby agree as follows:
      </p>

      <div class="sections-container">
        ${sectionsToRender.map((sec, idx) => {
          const num = sec.sectionNumber || String(idx + 1);
          const rawContent = (sec.content || "")
            .replace(/Longarm Technologies, LLC:\s*{{noticesLat}}/g, "{{noticesLat}}")
            .replace(/Partner\s*\([^)]*\):\s*{{noticesPartner}}/g, "{{noticesPartner}}");
          const replacedContent = replacePlaceholders(rawContent, variables);
          return `
            <div class="section-item">
              <div style="text-align: justify; text-justify: inter-word;">
                <span class="section-title">${num}. ${sec.title}.</span>
                <span class="section-content">${replacedContent}</span>
              </div>
            </div>
          `;
        }).join("\n")}
      </div>

      <div style="margin-top: 30px; page-break-inside: avoid;">
        <p style="font-weight: bold; text-align: justify;">
          IN WITNESS WHEREOF, the Parties represent and warrant that this Agreement is executed by duly authorized representatives of each Party as set forth below on the date first stated above.
        </p>

        <table class="signature-table">
          <tr>
            ${renderSignatureBlock(sigLat, primeCompanyName, "Dale Flippo / James Pasqualini", "President / Vice President")}
            ${renderSignatureBlock(sigPartner, agreement.partnerCompany || agreement.partnerName || "Partner Organization", "________________________", "Authorized Signatory")}
          </tr>
        </table>
      </div>
    </body>
    </html>
  `;
}

export function exportAgreementToWord(agreement, template, companyLogo) {
  const html = generateAgreementDocumentHtml(agreement, template, companyLogo, true);
  const blob = new Blob(["\ufeff" + html], {
    type: "application/msword"
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const fileName = `${(agreement.title || "Teaming_Agreement").replace(/[^a-zA-Z0-9_-]/g, "_")}.doc`;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function printAgreement(agreement, template, companyLogo) {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Please allow pop-ups to print or preview the agreement.");
    return;
  }

  const html = generateAgreementDocumentHtml(agreement, template, companyLogo, false);
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  try {
    printWindow.opener = null;
  } catch (e) {}
}
